import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react/components/shared/Icon";
import { useWebSocket } from "@circle-react/hooks/useWebSocket";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { useCreateChatMessage } from "../hooks/useCreateChatMessage";
import { useCreateLiveChat } from "../hooks/useCreateLiveChat";
import { useShowConversation } from "../hooks/useShowConversation";
import type { HelpWidgetMessage } from "../types/help_widget_message";
import { ChatMessage } from "./common/ChatMessage";
import { HelpInputArea } from "./common/HelpInputArea";
import { SkeletonLoader } from "./common/SkeletonLoader";

export const ConversationView = ({
  conversationId,
}: {
  conversationId: string;
}) => {
  const { data: conversation, isLoading } = useShowConversation(conversationId);
  const [messages, setMessages] = useState<HelpWidgetMessage[]>([]);
  const history = useHistory();
  useEffect(() => {
    if (conversation && messages.length === 0) {
      setMessages(conversation.messages);
    }
  }, [conversation, messages.length]);

  useWebSocket(
    {
      channel: "HelpWidget::ConversationChannel",
      onMessageReceive: (data: {
        event: string;
        message: HelpWidgetMessage;
      }) => {
        if (data.event === "newMessage") {
          setMessages(prevMessages => [...prevMessages, data.message]);
        }
      },
      conversation_id: conversationId,
      canCreateConnection: Boolean(conversationId),
    },
    [conversationId],
  );

  const { mutate: createChatMessage } = useCreateChatMessage({
    onSuccess: (message: HelpWidgetMessage) => {
      setMessages(prevMessages => [...prevMessages, message]);
    },
    onError: () => {},
  });
  const { mutate: createLiveChat } = useCreateLiveChat({
    onSuccess: () => {},
  });

  const extractButtons = (message: HelpWidgetMessage) => {
    if (message.content === "<ticket></ticket>") {
      return [
        {
          label: t("support_widget.chatbot.contact_methods.live_chat"),
          event: "select-contact-method",
          value: "live-chat",
          sublabel: t("support_widget.chatbot.estimated_wait_time", {
            time: "5-10 min",
          }),
          isPreferred: true,
          onClick: () => {
            createLiveChat({
              conversationId: message.help_widget_conversation_id,
            });
          },
        },
        {
          label: t("support_widget.chatbot.contact_methods.email"),
          event: "select-contact-method",
          value: "email",
          sublabel: t("support_widget.chatbot.estimated_wait_time", {
            time: "24 hrs",
          }),
          isPreferred: false,
          onClick: () => {
            history.push(`/conversations/${conversationId}/new_ticket`);
          },
        },
      ];
    }

    return [];
  };

  const onSendMessage = (text: string) => {
    createChatMessage({ conversationId, text });
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full flex-1 flex-col px-4">
        <SkeletonLoader rows={4} height="52px" wrapperClassName="!px-0" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col gap-5">
        {messages?.map((message: HelpWidgetMessage) => (
          <ChatMessage
            key={message.id}
            authorName={message.author_name}
            content={message.content}
            createdAt={message.created_at.toLocaleString()}
            type={
              message.author_type === "agent" ? "business" : message.author_type
            }
            buttons={extractButtons(message)}
            showDisclaimer={false}
          />
        ))}
      </div>
      <div className="sticky bottom-0 z-10 p-4">
        <HelpInputArea
          onSubmit={onSendMessage}
          placeholder={t(
            "help_widget.conversation_page.help_input_placeholder",
          )}
        />
      </div>
      <div className="sticky bottom-0 flex justify-center rounded-lg px-4 pb-4">
        <Typography.BodySm color="text-tertiary">
          {t("help_widget.conversation_page.footer_message")}
          <Icon type="info" size={14} />
        </Typography.BodySm>
      </div>
    </div>
  );
};
